import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FiUsers } from "react-icons/fi";
import useCustomers from "../../../marketplace_admin/libs/useCustomers";
import PageHeader from "../../components/PageHeader";
import FilterBar from "../../components/FilterBar";
import DataTable from "../../components/DataTable";
import EmptyState from "../../components/EmptyState";
import StatCard from "../../components/StatCard";
import YesNoBadge from "../../components/YesNoBadge";
import useInfiniteScrollRef from "../../utils/useInfiniteScrollRef";
import Spinner from "../../../Spinner/Spinner";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "active", label: "Active" },
  { id: "blocked", label: "Blocked" },
];

const AdminCustomersPage = () => {
  const { data, error, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useCustomers();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const lastRef = useInfiniteScrollRef(fetchNextPage, hasNextPage, isFetchingNextPage);

  const customers = useMemo(
    () => data?.pages?.flatMap((page) => page.customers || []) || [],
    [data]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return customers.filter((c) => {
      const name = `${c.first_name || ""} ${c.last_name || ""}`.toLowerCase();
      const matchesSearch =
        !term ||
        name.includes(term) ||
        (c.email || "").toLowerCase().includes(term) ||
        (c.phone_number || "").includes(term);
      if (!matchesSearch) return false;
      if (filter === "active") return c.isActive && !c.blocked;
      if (filter === "blocked") return c.blocked;
      return true;
    });
  }, [customers, search, filter]);

  const stats = useMemo(
    () => ({
      total: customers.length,
      active: customers.filter((c) => c.isActive && !c.blocked).length,
      blocked: customers.filter((c) => c.blocked).length,
      orders: customers.reduce((sum, c) => sum + (c.orderCount || 0), 0),
    }),
    [customers]
  );

  const columns = [
    {
      key: "id",
      label: "ID",
      render: (r) => r._id?.slice(0, 8).toUpperCase(),
    },
    {
      key: "name",
      label: "Customer",
      render: (r) => (
        <div>
          <div style={{ fontWeight: 600 }}>
            {[r.first_name, r.last_name].filter(Boolean).join(" ") || "—"}
          </div>
          <div style={{ fontSize: 13, color: "var(--dash-muted)" }}>{r.email}</div>
        </div>
      ),
    },
    {
      key: "phone",
      label: "Phone",
      render: (r) => [r.country_code, r.phone_number].filter(Boolean).join(" ") || "—",
    },
    {
      key: "orders",
      label: "Orders",
      render: (r) => r.orderCount ?? 0,
    },
    {
      key: "active",
      label: "Active",
      render: (r) => <YesNoBadge value={r.isActive} />,
    },
    {
      key: "blocked",
      label: "Blocked",
      render: (r) => <YesNoBadge value={r.blocked} variant="blocked" />,
    },
    {
      key: "actions",
      label: "Actions",
      render: (r) => (
        <Link to={`/admin/customers/${r._id}`} className="dashboard-btn dashboard-btn--primary">
          View details
        </Link>
      ),
    },
  ];

  return (
    <>
      <PageHeader title="Customers" subtitle="Browse buyer accounts and order history" icon={FiUsers} />

      {!isLoading && customers.length > 0 && (
        <div className="dashboard-stat-grid">
          <StatCard label="Loaded customers" value={stats.total} />
          <StatCard label="Active" value={stats.active} />
          <StatCard label="Blocked" value={stats.blocked} />
          <StatCard label="Total orders" value={stats.orders} />
        </div>
      )}

      <FilterBar searchValue={search} onSearchChange={setSearch} searchPlaceholder="Search by name, email, or phone…">
        <div className="dashboard-filter-pills">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              className={filter === f.id ? "active" : ""}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </FilterBar>

      {error && (
        <div className="dashboard-card">
          <p style={{ color: "var(--dash-danger)", margin: 0 }}>Could not load customers.</p>
        </div>
      )}

      {!isLoading && !error && filtered.length === 0 && (
        <EmptyState
          icon={FiUsers}
          title="No customers found"
          message={search || filter !== "all" ? "Try a different search or filter." : "No customers registered yet."}
        />
      )}

      {filtered.length > 0 && (
        <DataTable columns={columns} rows={filtered.map((c) => ({ ...c, _key: c._id }))} loading={isLoading} />
      )}

      {!isLoading && filtered.length > 0 && (
        <div ref={lastRef} className="dashboard-load-more">
          {isFetchingNextPage && <Spinner />}
          {!hasNextPage && !isFetchingNextPage && (
            <span style={{ color: "var(--dash-muted)", fontSize: 13 }}>All customers loaded</span>
          )}
        </div>
      )}
    </>
  );
};

export default AdminCustomersPage;
